/**
 * METABOLIC ENGINE V5 (CLEAN ROOM)
 * CORE SIMULATION ORCHESTRATOR
 * V5.3 Final: Multi-Day Continuous Thread Support
 */

import { toLogicDate } from '@/utils/date-utils';
import { ClinicalFraction, SimulationResultPoint } from './types';
import { V5_CONSTANTS as CLINICAL_K } from './constants';
import { EventParser } from './event-parser';
import { StateManager } from './state-manager';
import { GastricResolver } from './resolvers/gastric-resolver';
import { IntestinalResolver } from './resolvers/intestinal-resolver';
import { HormonalResolver } from './resolvers/hormonal-resolver';
import { ClearanceResolver } from './resolvers/clearance-resolver';
import { GlycogenResolver } from './resolvers/glycogen-resolver';
import { TriturationResolver } from './resolvers/trituration-resolver';
import { HepaticResolver } from './resolvers/hepatic-resolver';

export const MetabolicSimulatorV5 = {
    
    /**
     * simulate: Standard 48-hour window (Backward Compatible)
     */
    simulate(events: any[], targetDateStr: string, profile: any) {
        const [y, m, d] = targetDateStr.split('-').map(Number);
        const midnightUnix = new Date(y, m - 1, d, 0, 0, 0).getTime();
        const startUnix = midnightUnix - (1440 * 60000); // Start at Day -1
        
        const { meals, workouts } = EventParser.parseEvents(events, targetDateStr, midnightUnix);
        const state = StateManager.createEmptyState(profile?.initialGlycogenDebt || 0);
        const fractionKeys = StateManager.getFractionKeys();
        const results: SimulationResultPoint[] = [];

        for (let min = 0; min <= 2880; min++) {
            const currentUnix = startUnix + (min * 60000);
            this.processStep(min, currentUnix, state, meals, workouts, fractionKeys, profile);
            
            // Capture only the target day (1440 - 2880)
            if (min >= 1440 && min % 5 === 0) {
                results.push({
                    minute: min - 1440,
                    x: ((min - 1440) / 1440) * 100,
                    state: JSON.parse(JSON.stringify(state))
                });
            }
        }
        return results;
    },

    /**
     * simulateContinuous: Run engine for multiple days without resetting state.
     */
    simulateContinuous(events: any[], startUnix: number, durationDays: number, profile: any) {
        const endUnix = startUnix + (durationDays * 1440 * 60000);
        const { meals, workouts } = EventParser.parseEventsWindow(events, startUnix - 86400000, endUnix);
        
        const state = StateManager.createEmptyState(profile?.initialGlycogenDebt || 0);
        const fractionKeys = StateManager.getFractionKeys();
        const results: (SimulationResultPoint & { globalMin: number })[] = [];

        const totalMins = durationDays * 1440;
        for (let min = 0; min <= totalMins; min++) {
            const currentUnix = startUnix + (min * 60000);
            this.processStep(min, currentUnix, state, meals, workouts, fractionKeys, profile);
            
            if (min % 5 === 0) {
                results.push({
                    minute: min % 1440,
                    x: ((min % 1440) / 1440) * 100,
                    globalMin: min,
                    state: JSON.parse(JSON.stringify(state))
                });
            }
        }
        return results;
    },

    /**
     * processStep: Atomic unit of metabolic flux (1 minute).
     */
    processStep(min: number, currentUnix: number, state: any, meals: any[], workouts: any[], fractionKeys: string[], profile: any) {
        state.minute = min;

        // A. CIRCADIAN DYNAMICS
        const date = new Date(currentUnix);
        const minuteOfDay = date.getHours() * 60 + date.getMinutes();
        const circadianMod = 1.0 + 0.2 * Math.cos(2 * Math.PI * (minuteOfDay - 600) / 1440);

        // B. INTENSITY TRACKING
        state.intensity = 0;
        workouts.forEach(w => {
            if (currentUnix >= w.startUnix && currentUnix < w.endUnix) {
                state.intensity = Math.max(state.intensity, w.rpe / 10);
            } else if (currentUnix >= w.endUnix && currentUnix < w.endUnix + 7200000) {
                const decay = Math.max(0, 1 - ((currentUnix - w.endUnix) / 7200000));
                state.intensity = Math.max(state.intensity, (w.rpe / 10) * decay);
            }
        });

        // C. INGESTION (Flux Inflow)
        let bolusInput = 0;
        meals.forEach(m => {
            if (currentUnix >= m.startUnix && currentUnix < m.startUnix + (m.durationMins * 60000)) {
                const distribute = (frac: ClinicalFraction, isGastric: boolean, delay: number = 1.0) => {
                    const flow = m.fractions[frac] * m.rate * delay;
                    if (isGastric) state.fractions[frac].stomach += flow;
                    else state.fractions[frac].gut += flow;
                };
                distribute('intact_protein', true);
                distribute('complex_carbs', true, m.delayMod);
                distribute('lipids', true);
                distribute('fiber', true);
                distribute('collagen_peps', true);
                distribute('glucose', true, m.delayMod);
                distribute('fructose', true);
                distribute('free_amino_acids', true);
                bolusInput += (m.insulinLoad * m.rate);
            }
        });

        // D. PARALLEL RESOLUTION
        state.gastricVolume = fractionKeys.reduce((sum, f) => sum + state.fractions[f].stomach, 0);
        let lagMod = 1.0;
        meals.forEach(m => {
            if (currentUnix >= m.startUnix) {
                const minsSinceStart = (currentUnix - m.startUnix) / 60000;
                const mInflowMod = TriturationResolver.getLagMultiplier(minsSinceStart, m.totalCalories);
                lagMod = Math.min(lagMod, mInflowMod);
            }
        });

        const deltas = fractionKeys.map(f => ({
            fraction: f,
            emptied: GastricResolver.compute(state, f, CLINICAL_K.GASTRIC.rates[f] || 0.02, lagMod),
            absorbed: IntestinalResolver.compute(state, f, CLINICAL_K.ABSORPTION[f]),
            cleared: ClearanceResolver.compute(state, f, CLINICAL_K.CLEARANCE[f] || 0.01, circadianMod, profile)
        }));

        let totalEmptied = 0;
        deltas.forEach(d => {
            const comp = state.fractions[d.fraction as ClinicalFraction];
            totalEmptied += d.emptied;
            comp.stomach -= d.emptied;
            comp.gut += d.emptied;
            comp.gut -= d.absorbed;
            comp.blood += d.absorbed;
            const lagFlow = (comp.blood - comp.interstitium) * CLINICAL_K.DYNAMICS.INTERSTITIAL_LAG;
            comp.blood -= lagFlow;
            comp.interstitium += lagFlow;
            comp.blood -= d.cleared;
        });

        // E. ENDOCRINE & HEPATIC FEEDBACK
        const hgp = HepaticResolver.compute(state.insulin, state.fractions.lipids.blood, circadianMod);
        state.fractions.glucose.blood += hgp;

        let viscosityMod = 1.0;
        meals.forEach(m => {
            if (currentUnix >= m.startUnix && currentUnix < m.startUnix + (m.durationMins * 60000)) {
                if (m.delayMod < 1.0) viscosityMod = Math.min(viscosityMod, 0.6);
            }
        });

        const secreted = HormonalResolver.compute(state, totalEmptied, bolusInput, profile, viscosityMod);
        const sensitivityFactor = profile?.insulin_sensitivity === 'LOW' ? 0.7 : (profile?.insulin_sensitivity === 'HIGH' ? 1.3 : 1.0);
        const insClearance = state.insulin * ((CLINICAL_K.CLEARANCE.insulin || 0.08) * sensitivityFactor);
        state.insulin += (secreted - insClearance);
        if (state.insulin < 0.01) state.insulin = 0;

        const gCleared = deltas.find(d => d.fraction === 'glucose')?.cleared || 0;
        state.glycogenDebt += GlycogenResolver.computeDelta(state, gCleared, profile);
        if (state.glycogenDebt < 0) state.glycogenDebt = 0;
    }
};
